import type { ReactNode } from "react"
import { useEffect, useState } from "react"
import { apiGet, apiPost, type SessionState } from "../lib/api"
import { AppShell } from "./app-shell"

export function AuthenticatedAppShell({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string
  title: string
  children: (session: SessionState) => ReactNode
}) {
  const [session, setSession] = useState<SessionState | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    void apiGet<SessionState>("/api/me")
      .then((value) => {
        if (!cancelled) {
          setSession(value)
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason.message : "Failed to load session.")
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  async function handleLogout() {
    await apiPost("/api/logout")
    window.location.assign("/login")
  }

  return (
    <AppShell
      eyebrow={eyebrow}
      title={title}
      onLogout={handleLogout}
      showAdmin={session?.user?.isAdgiroEmployee ?? false}
    >
      {!session && !error ? (
        <div className="dashboard-card">
          <h2>Loading workspace</h2>
          <p>Checking your session and loading the current workspace state.</p>
        </div>
      ) : null}

      {error ? (
        <div className="dashboard-card">
          <h2>Session unavailable</h2>
          <p>{error}</p>
        </div>
      ) : null}

      {session && !session.authenticated ? (
        <div className="dashboard-card">
          <h2>Sign in required</h2>
          <p>Log in with your Google account to access your Adgiro workspace.</p>
          <div className="provider-buttons">
            <a href="/auth/login/google?next=/app" className="button">
              Continue with Google
            </a>
          </div>
        </div>
      ) : null}

      {session?.authenticated ? children(session) : null}
    </AppShell>
  )
}
